import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Grid, Card, Divider, Avatar, IconButton, Chip } from "@mui/material";
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  LocationOn,
  Email,
  Phone,
  Public,
  Badge,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MDSnackbar from "components/MDSnackbar";
import { useAuth } from "contexts/AuthContext";

// ProfileCard component with PropTypes
const ProfileCard = ({ children, title, action }) => (
  <Card
    sx={{
      borderRadius: "16px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
      p: 3,
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}
  >
    <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
      <MDTypography variant="h5" fontWeight="bold">
        {title}
      </MDTypography>
      {action}
    </MDBox>
    {children}
  </Card>
);

ProfileCard.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  action: PropTypes.node,
};

// InfoItem component with PropTypes
const InfoItem = ({ icon, label, value, editMode, name, onChange, section }) => (
  <MDBox mb={2.5} display="flex" alignItems="center">
    <MDBox mr={2} color="text" display="flex" alignItems="center">
      {icon}
    </MDBox>
    {editMode ? (
      <MDInput
        fullWidth
        value={value}
        name={name}
        onChange={(e) => onChange(e, section)}
        sx={{
          "& .MuiInputBase-input": {
            py: 1,
            fontSize: "0.875rem",
          },
        }}
      />
    ) : (
      <MDTypography variant="body2" color="text">
        {label}: <span style={{ fontWeight: 500, color: "#344767" }}>{value}</span>
      </MDTypography>
    )}
  </MDBox>
);

InfoItem.propTypes = {
  icon: PropTypes.node.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  editMode: PropTypes.bool,
  name: PropTypes.string,
  onChange: PropTypes.func,
  section: PropTypes.string,
};

function Profile() {
  const { user, church, updateProfile } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    user: { name: "", email: "", role: "" },
    church: { name: "", address: "", email: "", telephone: "", gps: "", abbreviation: "" },
  });
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);

  useEffect(() => {
    if (user && church) {
      setFormData({
        user: {
          name: user.name || "",
          email: user.email || "",
          role: user.role || "",
        },
        church: {
          name: church.name || "",
          address: church.address || "",
          email: church.email || "",
          telephone: church.telephone || "",
          gps: church.gps || "",
          abbreviation: church.abbreviation || "",
        },
      });
    }
  }, [user, church]);

  const handleChange = (e, section) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [section]: { ...prev[section], [name]: value },
    }));
  };

  const handleSubmit = async () => {
    try {
      await updateProfile(formData.user, formData.church);
      setSuccessSB(true);
      setEditMode(false);
    } catch (error) {
      setErrorSB(true);
    }
  };

  if (!user || !church) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3} textAlign="center">
          <MDTypography variant="h6">Loading profile data...</MDTypography>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Grid container spacing={3}>
          {/* User Profile Section */}
          <Grid item xs={12} md={5}>
            <ProfileCard
              title="My Profile"
              action={
                <IconButton
                  onClick={() => setEditMode(!editMode)}
                  sx={{
                    backgroundColor: editMode ? "error.main" : "info.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: editMode ? "error.dark" : "info.dark",
                    },
                  }}
                >
                  {editMode ? <CloseIcon /> : <EditIcon />}
                </IconButton>
              }
            >
              <MDBox display="flex" flexDirection="column" alignItems="center" mb={3}>
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    bgcolor: "primary.main",
                    fontSize: "2rem",
                    mb: 2,
                  }}
                >
                  {user.name.charAt(0)}
                </Avatar>
                {editMode ? (
                  <MDInput
                    fullWidth
                    name="name"
                    value={formData.user.name}
                    onChange={(e) => handleChange(e, "user")}
                    sx={{ textAlign: "center", mb: 2 }}
                  />
                ) : (
                  <MDTypography variant="h5" fontWeight="bold" textAlign="center">
                    {user.name}
                  </MDTypography>
                )}
                <Chip
                  label={user.role.toUpperCase()}
                  color="primary"
                  size="small"
                  sx={{ borderRadius: 1 }}
                />
              </MDBox>

              <Divider sx={{ my: 2 }} />

              <InfoItem
                icon={<Email fontSize="small" />}
                label="Email"
                value={user.email}
                editMode={editMode}
                name="email"
                onChange={handleChange}
                section="user"
              />

              <InfoItem
                icon={<Badge fontSize="small" />}
                label="Role"
                value={user.role}
                editMode={false} // Role not editable
              />
            </ProfileCard>
          </Grid>

          {/* Church Information Section */}
          <Grid item xs={12} md={7}>
            <ProfileCard title="Church Information">
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InfoItem
                    icon={<Badge fontSize="small" />}
                    label="Church Name"
                    value={editMode ? formData.church.name : church.name}
                    editMode={editMode}
                    name="name"
                    onChange={handleChange}
                    section="church"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem
                    icon={<Public fontSize="small" />}
                    label="Abbreviation"
                    value={editMode ? formData.church.abbreviation : church.abbreviation}
                    editMode={editMode}
                    name="abbreviation"
                    onChange={handleChange}
                    section="church"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoItem
                    icon={<LocationOn fontSize="small" />}
                    label="Address"
                    value={editMode ? formData.church.address : church.address}
                    editMode={editMode}
                    name="address"
                    onChange={handleChange}
                    section="church"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem
                    icon={<Email fontSize="small" />}
                    label="Church Email"
                    value={editMode ? formData.church.email : church.email}
                    editMode={editMode}
                    name="email"
                    onChange={handleChange}
                    section="church"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InfoItem
                    icon={<Phone fontSize="small" />}
                    label="Telephone"
                    value={editMode ? formData.church.telephone : church.telephone}
                    editMode={editMode}
                    name="telephone"
                    onChange={handleChange}
                    section="church"
                  />
                </Grid>
                <Grid item xs={12}>
                  <InfoItem
                    icon={<LocationOn fontSize="small" />}
                    label="GPS Coordinates"
                    value={editMode ? formData.church.gps : church.gps}
                    editMode={editMode}
                    name="gps"
                    onChange={handleChange}
                    section="church"
                  />
                </Grid>
              </Grid>
            </ProfileCard>
          </Grid>
        </Grid>

        {editMode && (
          <MDBox mt={3} display="flex" justifyContent="flex-end">
            <MDButton
              variant="gradient"
              color="success"
              startIcon={<CheckIcon />}
              onClick={handleSubmit}
              sx={{
                borderRadius: "12px",
                px: 4,
                py: 1.5,
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            >
              Save Changes
            </MDButton>
          </MDBox>
        )}
      </MDBox>

      {/* Notifications */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Success"
        content="Profile updated successfully"
        open={successSB}
        onClose={() => setSuccessSB(false)}
        close={() => setSuccessSB(false)}
        bgWhite
      />

      <MDSnackbar
        color="error"
        icon="warning"
        title="Error"
        content="Failed to update profile"
        open={errorSB}
        onClose={() => setErrorSB(false)}
        close={() => setErrorSB(false)}
        bgWhite
      />

      <Footer />
    </DashboardLayout>
  );
}

export default Profile;
